import { useEffect, useState } from 'react';
import { PastEvent, fetchPastEvents } from '../services/pastEventService';

export default function PastEvents() {
  const [pastEvents, setPastEvents] = useState<PastEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Functie voor het correct weergeven van afbeeldingspaden
  const getImageUrl = (url: string): string => {
    if (!url) return '/images/aikido-social.jpg';
    
    // Als het een volledige URL is, gebruik deze direct
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Als het pad nog niet begint met /images/, voeg dit toe
    if (!url.startsWith('/images/') && !url.startsWith('/')) {
      return `/images/${url}`;
    }
    
    return url;
  };

  useEffect(() => {
    const loadPastEvents = async () => {
      try {
        console.log('PastEvents: Starting to fetch past events');
        const events = await fetchPastEvents();
        
        // Debug informatie voor het Ivan Marinovic event
        const ivanEvent = events.find(e => 
          e.title.toLowerCase().includes('ivan') || 
          e.title.toLowerCase().includes('marinovic')
        );
        
        if (ivanEvent) {
          console.log('FOUND IVAN EVENT:', ivanEvent);
          console.log('IMAGE URL:', ivanEvent.image);
        } else {
          console.log('NO IVAN EVENT FOUND IN:', events.map(e => e.title));
        }
        
        console.log('PastEvents: Received events:', events);
        setPastEvents(events);
        setError(null);
      } catch (err: unknown) {
        const errorObj = err as Error;
        const errorMessage = errorObj.message || 'Er is een probleem opgetreden bij het laden van de evenementen.';
        setError(errorMessage);
        console.error('Error loading past events:', errorObj);
      } finally {
        setLoading(false);
      }
    };

    loadPastEvents();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
        <p>Laden...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
      <h1 className="text-4xl font-bold mb-8">Eerdere Evenementen</h1>
      
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {pastEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="h-52 w-full overflow-hidden">
              <img
                src={getImageUrl(event.image)}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onLoad={(e) => {
                  // Log wanneer een afbeelding succesvol is geladen
                  if (event.title.toLowerCase().includes('ivan') || 
                      event.title.toLowerCase().includes('marinovic')) {
                    console.log('IVAN IMAGE LOADED SUCCESSFULLY:', event.image);
                    console.log('Image element:', e.currentTarget);
                  }
                }}
                onError={(e) => {
                  console.error('Afbeelding kon niet worden geladen:', event.image);
                  if (event.title.toLowerCase().includes('ivan') || 
                      event.title.toLowerCase().includes('marinovic')) {
                    console.error('IVAN IMAGE FAILED TO LOAD!');
                  }
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/aikido-social.jpg';
                }}
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
              <div className="flex flex-wrap mb-3">
                <span className="text-gray-600 mr-4">{event.date}</span>
                {event.startTime && (
                  <span className="text-gray-600 mr-4">
                    {event.startTime}{event.endTime ? ` - ${event.endTime}` : ''} uur
                  </span>
                )}
                {event.category && (
                  <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                    {event.category}
                  </span>
                )}
              </div>
              
              {event.teacher && (
                <p className="text-gray-600 mb-2">
                  <span className="font-medium">Docent:</span> {event.teacher}
                </p>
              )}
              
              <p className="text-gray-600 mb-4">
                <span className="font-medium">Locatie:</span> {event.location}
              </p>
              
              <div className="text-gray-700 mb-4">
                {event.description}
              </div>
              
              <div className="flex flex-wrap gap-2 mt-4">
                {event.eventLink && (
                  <a 
                    href={event.eventLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-300"
                  >
                    Meer informatie
                  </a>
                )}
                
                {event.registrationLink && (
                  <a 
                    href={event.registrationLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-300"
                  >
                    Inschrijven
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
